import React, { useEffect } from 'react';

const TermsAndConditionsPage = () => {
  useEffect(() => {
    document.title = 'Terms and Conditions | SIDEO QR';
  }, []);

  return (
    <main className="min-h-screen bg-slate-50 py-10 px-4 sm:px-6 lg:px-8">
      <article className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8 lg:p-10">
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Barlow' }}>
            Terms and Conditions
          </h1>
          <p className="text-sm text-slate-500 mt-2">Last Updated: March 24, 2026</p>
        </header>

        <div className="space-y-7 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">1. Introduction</h2>
            <p>
              SIDEO provides QR-based communication services that allow vehicle owners to be contacted via call or
              message without publicly displaying their phone number.
            </p>
            <p className="mt-2">
              By accessing or using SIDEO services, you agree to be bound by these Terms and Conditions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">2. Definitions</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>"SIDEO"</strong> refers to the platform providing QR-based contact services.</li>
              <li><strong>"User"</strong> refers to both vehicle owners and individuals scanning the QR code.</li>
              <li><strong>"Owner"</strong> refers to the vehicle owner using a SIDEO QR sticker.</li>
              <li><strong>"Scanner"</strong> refers to any individual who scans the QR code to contact the owner.</li>
              <li><strong>"Service"</strong> refers to the communication system enabling calls or messages between users.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">3. Eligibility</h2>
            <p>By using SIDEO, you confirm that:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>You are legally capable of entering into a binding agreement under applicable Indian laws</li>
              <li>You will use the service only for lawful purposes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">4. Service Description</h2>
            <p>SIDEO provides:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>A physical QR code sticker for vehicles</li>
              <li>A digital system that enables communication between scanner and owner</li>
              <li>Options to contact via call or message</li>
            </ul>
            <p className="mt-2">The service:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Requires activation after purchase</li>
              <li>Is valid for a defined period (e.g., 1 year)</li>
              <li>May require renewal to continue usage</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">5. Consent-Based Communication</h2>
            <p>SIDEO operates on a consent-based communication model:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Scanners may choose to contact the owner via call or message</li>
              <li>If a scanner chooses to initiate a call, their phone number will be shared with the owner only after explicit consent</li>
              <li>By selecting the call option and proceeding, the scanner provides explicit consent to share their phone number with the vehicle owner for the purpose of communication</li>
              <li>Messaging may be provided without sharing the phone number</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">6. User Responsibilities</h2>
            <p className="font-medium mb-1">For Vehicle Owners:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provide accurate and updated contact information</li>
              <li>Use the service responsibly</li>
              <li>Not misuse or abuse the contact details of scanners</li>
            </ul>
            <p className="font-medium mt-3 mb-1">For Scanners:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Use the service only for genuine and lawful purposes</li>
              <li>Not engage in abusive, threatening, or inappropriate behavior</li>
              <li>Respect the privacy of the vehicle owner</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">7. Acceptable Use</h2>
            <p>Users agree NOT to:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Harass, abuse, threaten, or intimidate others</li>
              <li>Use the service for illegal, fraudulent, or harmful activities</li>
              <li>Repeatedly contact users in a way that constitutes spam</li>
            </ul>
            <p className="mt-2">SIDEO reserves the right to restrict, suspend, or block users in case of misuse.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">8. Privacy &amp; Data Usage</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Phone numbers are not publicly displayed</li>
              <li>Data is used solely to enable communication between users</li>
              <li>SIDEO does not sell personal data to third parties</li>
              <li>Data may be processed to provide the service or comply with legal obligations</li>
              <li>Users may request data deletion or account deactivation by contacting: <a href="mailto:support@sideo.in" className="text-black hover:underline">support@sideo.in</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">9. Communication Disclaimer</h2>
            <p>SIDEO acts only as a facilitator of communication between users. SIDEO:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Does not control or monitor conversations</li>
              <li>Is not responsible for the content of calls or messages</li>
              <li>Does not guarantee the identity or intent of users</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">10. Limitation of Liability</h2>
            <p>SIDEO shall not be liable for:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Any misuse of the service by users</li>
              <li>Any disputes between users</li>
              <li>Any loss, damage, or incident related to vehicles</li>
              <li>Any indirect or consequential damages arising from use of the service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">11. Service Availability</h2>
            <p>
              The service depends on internet and telecom networks. SIDEO does not guarantee uninterrupted or
              error-free operation.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">12. Payments &amp; Validity</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>The QR sticker is a one-time purchase</li>
              <li>The service includes a defined validity period</li>
              <li>Continued use may require renewal</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">13. Refund &amp; Replacement</h2>
            <p>Refunds or replacements are governed by the Refund &amp; Replacement Policy.</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Refunds may be provided in cases such as damaged or undelivered products</li>
              <li>No refunds may be provided after activation of the QR code</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">14. Suspension &amp; Termination</h2>
            <p>SIDEO may suspend or terminate access if:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>These Terms are violated</li>
              <li>Misuse, abuse, or suspicious activity is detected</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">15. Intellectual Property</h2>
            <p>
              All trademarks, branding, and technology associated with SIDEO are owned by SIDEO and may not be used
              without prior permission.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">16. Governing Law &amp; Jurisdiction</h2>
            <p>
              These Terms are governed by the laws of India. Any disputes shall be subject to the jurisdiction of
              courts in Bhopal, Madhya Pradesh.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">17. Changes to Terms</h2>
            <p>
              SIDEO reserves the right to update or modify these Terms at any time. Continued use of the service
              after changes constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">18. Contact</h2>
            <p>For any queries, support, or data-related requests:</p>
            <ul className="list-none pl-0 mt-2 space-y-1">
              <li>Email: <a href="mailto:support@sideo.in" className="text-black hover:underline">support@sideo.in</a></li>
              <li>Website: <a href="https://www.sideo.in" target="_blank" rel="noopener noreferrer" className="text-black hover:underline">www.sideo.in</a></li>
            </ul>
          </section>
        </div>
      </article>
    </main>
  );
};

export default TermsAndConditionsPage;
